import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import theme from '@/theme';
import styled from '@emotion/styled';
import { ExpandMore } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import { MenuProps } from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { ReactElement, ReactNode, useState } from 'react';
import { FilterChip } from './FilterChip';

const StyledSelect = styled(Select)`
    padding: 0;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 50px !important;
    color: ${theme.colors.secondary[700]};
    cursor: pointer;

    :hover {
        border: 1px solid ${theme.colors.secondary[900]};
    }

    fieldset {
        border: none;
    }

    .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input {
        padding: 0 12px;
    }

    .MuiSvgIcon-root {
        color: ${theme.colors.secondary[900]};
    }
` as unknown as typeof Select;

const StyledMenuItem = styled(MenuItem)`
    margin: 6px 8px;
    padding: 0 12px;
    border-radius: 8px;
    gap: 6px;
    align-items: center;

    &.Mui-selected,
    &.Mui-selected.Mui-focusVisible {
        background-color: rgba(0, 0, 0, 0.04);

        :hover {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }

    img,
    svg {
        margin-left: -4px;
    }
`;

const SecondaryText = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${theme.colors.black[500]};
    margin-left: 1px;
`;

export type FilterWithDividerOption = {
    value: string;
    isDivider: true;
    chipLabel?: never;
    icon?: never;
    skipListIcon?: never;
};

export type FilterWithoutDividerOption = {
    value: string;
    label: string;
    secondaryLabel?: string;
    chipLabel?: string;
    icon?: string | ReactNode;
    skipListIcon?: boolean;
    isDivider?: false;
};

export type FilterOption = FilterWithDividerOption | FilterWithoutDividerOption;

interface Props extends Omit<SelectProps<string>, 'value'> {
    value: FilterOption;
    options: FilterOption[];
    onChange: (e: SelectChangeEvent<string>) => void;
    sxPaperProps?: SxProps<Theme>;
    transformValue?: (value: FilterWithoutDividerOption) => FilterOption;
    menuProps?: Partial<MenuProps>;
    IconComponent?: React.ComponentType<any>;
}

export const FilterChipSelect = ({
    value,
    options,
    onChange,
    sxPaperProps,
    transformValue,
    onOpen,
    onClose,
    className,
    menuProps,
    IconComponent,
    ...props
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    const renderValue = (selected: unknown): ReactElement => {
        const selectedOption = options.find(option => option.value === selected);

        const transformedValue = transformValue
            ? transformValue((selectedOption || value) as FilterWithoutDividerOption)
            : selectedOption || value;

        return <FilterChip option={transformedValue} />;
    };

    const handleOpen = (e: React.SyntheticEvent) => {
        onOpen?.(e);
        setIsOpen(true);
    };

    const handleClose = (e: React.SyntheticEvent) => {
        onClose?.(e);
        setIsOpen(false);
    };

    return (
        <StyledSelect
            className={`${className || ''} ${isOpen ? 'is-open' : ''}`}
            onOpen={handleOpen}
            onClose={handleClose}
            MenuProps={{
                PaperProps: { sx: { borderRadius: '12px !important', ...sxPaperProps } },
                ...menuProps,
            }}
            value={value.value}
            onChange={onChange}
            renderValue={renderValue}
            IconComponent={IconComponent || ExpandMore}
            {...props}>
            {options.map((option, index) => {
                if (option.isDivider) {
                    return <Divider key={`divider-${index}`} sx={{ my: 1, mx: 2 }} />;
                }

                const icon =
                    option.icon && !option.skipListIcon ? (
                        typeof option.icon === 'string' ? (
                            <MaskedIcon src={option.icon} size="16px" />
                        ) : (
                            <>{option.icon}</>
                        )
                    ) : null;

                return (
                    <StyledMenuItem key={option.value} value={option.value}>
                        {icon}
                        {option.label}
                        {option.secondaryLabel && <SecondaryText>{option.secondaryLabel}</SecondaryText>}
                    </StyledMenuItem>
                );
            })}
        </StyledSelect>
    );
};
